import type { CustomScrollbarProps } from '@lved/ui/components/shared/ui/CustomScrollbar';
import type { ResizeHandleProps } from '@lved/ui/components/shared/ui/ResizeHandle';
import { ReactWidget } from '@theia/core/lib/browser';
import { injectable, postConstruct } from '@theia/core/shared/inversify';
import { ComponentType } from 'react';

type DesignPanelModule = {
    DesignEventBridge: ComponentType;
    AddNewScreen: ComponentType<{ className?: string }>;
    NavigatorPanel: ComponentType;
    PropertiesPanel: ComponentType;
    CustomScrollbar: ComponentType<CustomScrollbarProps>;
    ResizeHandle: ComponentType<ResizeHandleProps>;
};

/**
 * Left-sidebar panel for design mode.
 *
 * Renders the layer-navigator (element tree) and the element properties form
 * from @lved/ui, together with the DesignEventBridge that wires canvas events
 * to the shared design stores.
 *
 * This widget is hidden in code mode via a CSS [data-lvgl-layout] rule and its
 * contribution does not register a menu action for code mode.
 */
@injectable()
export class DesignPanel extends ReactWidget {
    static readonly ID: string = 'widget:lvgl-design-panel';
    static readonly LABEL: string = 'Design';

    private module: DesignPanelModule | undefined;
    private loadError: string | undefined;

    @postConstruct()
    protected init(): void {
        this.id = DesignPanel.ID;
        this.title.label = DesignPanel.LABEL;
        this.title.caption = DesignPanel.LABEL;
        this.title.closable = false;
        this.title.iconClass = 'codicon codicon-cursor';
        this.doInit();
    }

    protected async doInit(): Promise<void> {
        try {
            const [eventBridgeMod, addNewScreenMod, navigatorMod, propertiesMod, scrollbarMod, resizeMod] =
                await Promise.all([
                    import('@lved/ui/components/shared/ui/Design/DesignEventBridge'),
                    import('@lved/ui/components/shared/ui/Design/AddNewScreen'),
                    import('@lved/ui/components/shared/ui/Design/NavigatorPanel'),
                    import('@lved/ui/components/shared/ui/Design/PropertiesPanel'),
                    import('@lved/ui/components/shared/ui/CustomScrollbar'),
                    import('@lved/ui/components/shared/ui/ResizeHandle')
                ]);
            this.module = {
                DesignEventBridge: (eventBridgeMod as unknown as { default: ComponentType }).default,
                AddNewScreen: (addNewScreenMod as unknown as { default: ComponentType<{ className?: string }> })
                    .default,
                NavigatorPanel: (navigatorMod as unknown as { default: ComponentType }).default,
                PropertiesPanel: (propertiesMod as unknown as { default: ComponentType }).default,
                CustomScrollbar: (scrollbarMod as unknown as { default: ComponentType<CustomScrollbarProps> }).default,
                ResizeHandle: (resizeMod as unknown as { default: ComponentType<ResizeHandleProps> }).default
            };
        } catch (error: unknown) {
            this.loadError = error instanceof Error ? error.message : 'Failed to load Design panel.';
        }
        this.update();
    }

    protected render(): JSX.Element {
        if (this.loadError) {
            return <p style={{ padding: '8px', color: 'var(--theia-errorForeground)' }}>{this.loadError}</p>;
        }
        if (!this.module) {
            return <p style={{ padding: '8px' }}>Loading Design panel…</p>;
        }

        const { DesignEventBridge, AddNewScreen, NavigatorPanel, PropertiesPanel, CustomScrollbar, ResizeHandle } =
            this.module;

        return (
            <div
                className="lvgl-design-panel-root"
                style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
            >
                <DesignEventBridge />
                <AddNewScreen />
                <CustomScrollbar className="lvgl-panel-navigator-scroll">
                    <NavigatorPanel />
                </CustomScrollbar>
                <ResizeHandle resizeTarget="previous" orientation="horizontal" />
                <CustomScrollbar className="lvgl-panel-properties-scroll">
                    <PropertiesPanel />
                </CustomScrollbar>
            </div>
        );
    }
}
